import { Check, ChevronLeft, ChevronRight, ExternalLink, Maximize2, Star } from "lucide-react";
import { paletteFaceIcon } from "@/shared/components/SentimentBadge";
import { useEffect, useMemo, useRef, useState } from "react";
import { apiClient } from "@/shared/api/api-client";
import { errorMessage } from "@/shared/api/http";
import { HighlightedText } from "@/shared/components/HighlightedText";
import { mentionPlatform } from "@/shared/components/mention-platform";
import { MentionClient } from "@/features/mentions/MentionClient";
import { hasPlatformLogo, PlatformLogo } from "@/shared/components/PlatformLogo";
import { usePolarityChoices } from "@/shared/components/polarity-choices";
import { polarityPalette } from "@/shared/types/polarity";
import type { Mention } from "@/shared/types/mention";
import { ToolbarSelect } from "@/shared/components/ToolbarSelect";
import { MentionActionsMenu } from "./MentionActionsMenu";
import { MentionDetailsBody } from "./MentionJustificationsModal";
import { MentionInlineCatalog, type CatalogKind, type MentionCatalog } from "./MentionInlineCatalog";
import { MentionPublisher } from "./mention-parts";
import { MentionFullTextDialog } from "./MentionFullTextDialog";
import { GenerateSummary, SummaryText } from "./MentionSummary";
import { useMentionSummary } from "./use-mention-summary";
import type { MentionTextView } from "@/shared/vocabulary/mention-text-views";

/** As três listas editáveis na linha, na ordem da faixa do card. */
const NO_MENTION = { id: -1, summary: null, summaryOrigin: null };

const CATALOG_ROWS: Array<{ kind: CatalogKind; label: string }> = [
  { kind: "themes", label: "Subtemas" },
  { kind: "keyMessages", label: "Mensagens-chave" },
  { kind: "spokespersons", label: "Porta-vozes" }
];

const APPROACHES = [
  // Do pior pro melhor, a ordem da lista (2026-10-15)
  { value: "citation", label: "Citação", key: "Q", stars: 1 },
  { value: "supporting", label: "Coadjuvante", key: "W", stars: 2 },
  { value: "protagonist", label: "Protagonista", key: "E", stars: 3 }
] as const;

type Override = { polarity?: string | null; approach?: string | null; reviewed?: boolean };

/**
 * O "Modo revisão" da tela de Menções (2026-10-13): fila à esquerda, a
 * menção aberta no centro com a classificação editável em um clique, e
 * o detalhe (CENA, porquê, histórico) à direita — pra conferir uma a
 * uma sem abrir e fechar o editor. Inspirado no SmartCare do Stilingue.
 *
 * Não tem rota nem dado próprio: lê a MESMA página de menções da lista e
 * grava pelas mesmas portas do editor (`updateMentionClassification`) e
 * do menu da linha (revisar, descartar, vincular). Temas, mensagens-chave
 * e porta-vozes se editam na linha desde 2026-10-18, pelo mesmo
 * `MentionInlineCatalog` do card (× tira, "+" abre o catálogo da marca,
 * que o pai carrega uma vez por marca); o texto inteiro abre no modal.
 *
 * Teclado (fora de campo de texto e sem diálogo aberto): ↑/↓ ou K/J
 * andam; 1–5 escolhem a polaridade na ordem da escala; Q/W/E a
 * abordagem; R marca revisada e passa pra próxima.
 */
export function MentionReviewMode({
  mentions,
  token,
  onReload,
  onToggleReviewed,
  onToggleDiscard,
  onLink,
  onError,
  actionMenuId,
  onToggleActionMenu,
  onCloseActionMenu,
  page,
  pageSize,
  pageSizes,
  onPageSize,
  pageCount,
  total,
  canPrevPage,
  canNextPage,
  onPrevPage,
  onNextPage,
  loadCatalog,
  companyLogoUrl = null,
  textView = "post"
}: {
  /** Como mostrar o texto (preferência da pessoa, 2026-10-31). */
  textView?: MentionTextView;
  /** O logo da empresa aberta (a "foto" do cliente, como no card da lista). */
  companyLogoUrl?: string | null;
  mentions: Mention[];
  token: string;
  /** O catálogo da marca da menção, pro "+" das três listas — o mesmo
   *  `loadCatalog` do card, com a promessa guardada por marca na página. */
  loadCatalog: (mention: Mention) => Promise<MentionCatalog>;
  /** A menção foi corrigida: a página a atualiza NO LUGAR (não some por
   *  deixar de casar com o filtro — 2026-11-03). */
  onReload: (mentionId: number) => void;
  onToggleReviewed: (mention: Mention) => Promise<void>;
  onToggleDiscard: (mention: Mention) => void | Promise<void>;
  onLink: (mention: Mention) => void;
  onError: (message: string) => void;
  actionMenuId: number | null;
  onToggleActionMenu: (id: number) => void;
  onCloseActionMenu: () => void;
  page: number;
  pageSize: number;
  pageSizes: readonly number[];
  onPageSize: (size: number) => void;
  pageCount: number;
  total: number;
  canPrevPage: boolean;
  canNextPage: boolean;
  onPrevPage: () => void;
  onNextPage: () => void;
}) {
  // Do pior pro melhor, como na lista (o hook entrega do melhor pro pior).
  // Os atalhos 1–4 seguem a ordem da tela.
  const choices = usePolarityChoices();
  const polarities = useMemo(() => [...choices].reverse(), [choices]);
  const [selectedId, setSelectedId] = useState<number | null>(mentions[0]?.id ?? null);
  const [overrides, setOverrides] = useState<Map<number, Override>>(() => new Map());
  // O texto inteiro num modal (2026-10-19): o foco mostra o trecho da lista.
  const [fullTextOpen, setFullTextOpen] = useState(false);
  // Ao trocar de página pra trás, a seleção cai na ÚLTIMA da nova página.
  const landAtEnd = useRef(false);
  const queueRef = useRef<HTMLDivElement>(null);

  // Dados novos do servidor: o otimista sai, e a seleção se mantém se a
  // menção ainda está na página (senão, primeira ou última).
  useEffect(() => {
    setOverrides(new Map());
    setSelectedId(current => {
      if (current !== null && mentions.some(mention => mention.id === current)) return current;
      const fallback = landAtEnd.current ? mentions[mentions.length - 1] : mentions[0];
      landAtEnd.current = false;
      return fallback?.id ?? null;
    });
  }, [mentions]);

  const index = mentions.findIndex(mention => mention.id === selectedId);
  const base = index >= 0 ? mentions[index] : null;
  const override = base ? overrides.get(base.id) : undefined;
  // O resumo da IA (2026-10-31): com a preferência em "resumo" e resumo na
  // menção ele toma o lugar do trecho; sem ele, "Gerar resumo". O modal do
  // texto completo continua mostrando a postagem.
  const summaryState = useMentionSummary(base ?? NO_MENTION, token);
  const showSummary = textView === "summary" && summaryState.summary !== null;
  const canGenerateSummary =
    textView === "summary" && summaryState.summary === null && base !== null && !base.discardedAt;
  const view = useMemo(() => {
    if (!base) return null;
    const polarity = override && "polarity" in override ? override.polarity ?? null : base.polarity;
    const approach = override && "approach" in override ? override.approach ?? null : base.approachValue;
    const reviewed = override?.reviewed ?? base.reviewedAt !== null;
    return { polarity, approach, reviewed };
  }, [base, override]);

  // A linha da fila acompanha a seleção quando ela muda pelo teclado.
  useEffect(() => {
    queueRef.current
      ?.querySelector<HTMLElement>(`[data-mention-id="${selectedId}"]`)
      ?.scrollIntoView?.({ block: "nearest" });
  }, [selectedId]);

  const go = (step: 1 | -1) => {
    if (index < 0) return;
    const next = index + step;
    if (next >= 0 && next < mentions.length) {
      setSelectedId(mentions[next].id);
    } else if (step === 1 && canNextPage) {
      onNextPage();
    } else if (step === -1 && canPrevPage) {
      landAtEnd.current = true;
      onPrevPage();
    }
  };

  const patch = async (mention: Mention, change: Override, request: Parameters<typeof apiClient.updateMentionClassification>[2]) => {
    setOverrides(current => new Map(current).set(mention.id, { ...current.get(mention.id), ...change }));
    try {
      await apiClient.updateMentionClassification(token, mention.id, request);
      onReload(mention.id);
    } catch (failure) {
      setOverrides(current => {
        const next = new Map(current);
        next.delete(mention.id);
        return next;
      });
      onError(errorMessage(failure, "Não foi possível salvar a classificação."));
    }
  };

  const setPolarity = (name: string) => {
    if (!base || base.discardedAt || !view || view.polarity === name) return;
    void patch(base, { polarity: name }, { polarity: name });
  };
  const setApproach = (value: string) => {
    if (!base || base.discardedAt || !view || view.approach === value) return;
    void patch(base, { approach: value }, { approach: value });
  };
  const markReviewedAndNext = async () => {
    if (!base || base.discardedAt || !view) return;
    const marking = !view.reviewed;
    setOverrides(current => new Map(current).set(base.id, { ...current.get(base.id), reviewed: marking }));
    await onToggleReviewed(base);
    if (marking) go(1);
  };

  // Atalhos. Ficam calados dentro de campo de texto e com diálogo aberto
  // (editor, vincular, filtros), pra não roubar a digitação de ninguém.
  const handlers = useRef({ go, setPolarity, setApproach, markReviewedAndNext });
  handlers.current = { go, setPolarity, setApproach, markReviewedAndNext };
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target;
      if (target instanceof Element && target.closest("input, textarea, select, [contenteditable='true']")) return;
      if (document.querySelector("[role='dialog'], .drawer:not(.filters-panel)")) return;
      const key = event.key;
      const h = handlers.current;
      if (key === "ArrowDown" || key === "j" || key === "J") { event.preventDefault(); h.go(1); }
      else if (key === "ArrowUp" || key === "k" || key === "K") { event.preventDefault(); h.go(-1); }
      else if (key === "r" || key === "R") { event.preventDefault(); void h.markReviewedAndNext(); }
      else if (/^[1-9]$/.test(key)) {
        const choice = polarities[Number(key) - 1];
        if (choice) { event.preventDefault(); h.setPolarity(choice.name); }
      } else {
        const approach = APPROACHES.find(item => item.key === key.toUpperCase());
        if (approach) { event.preventDefault(); h.setApproach(approach.value); }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [polarities]);

  if (!mentions.length) return null;

  const platform = base ? mentionPlatform(base) : null;
  const PlatformIcon = platform?.Icon;
  const untitled = base?.title === "Publicação sem título";
  const link = base ? base.originalLink ?? base.link : null;

  return (
    <div className="review-mode">
      <aside className="review-queue" ref={queueRef} aria-label="Fila de menções">
        {mentions.map(mention => {
          const own = overrides.get(mention.id);
          const polarity = own && "polarity" in own ? own.polarity ?? null : mention.polarity;
          const reviewed = own?.reviewed ?? mention.reviewedAt !== null;
          const item = mentionPlatform(mention);
          const ItemIcon = item.Icon;
          return (
            <button
              type="button"
              key={mention.id}
              data-mention-id={mention.id}
              className={`review-queue-item${mention.id === selectedId ? " is-active" : ""}`}
              aria-current={mention.id === selectedId}
              onClick={() => setSelectedId(mention.id)}
            >
              <span className={`review-queue-logo net-${item.tone}`} title={item.label} aria-label={item.label} role="img">
                {hasPlatformLogo(item.tone) ? <PlatformLogo tone={item.tone} size={14} mono/> : <ItemIcon size={14} aria-hidden="true"/>}
              </span>
              <span className="review-queue-body">
                <span className="review-queue-top">
                  <strong>{mention.source}</strong>
                  <span className="review-queue-date">{mention.date}</span>
                </span>
                <span className="review-queue-brand">{mention.brandName}</span>
                <span className="review-queue-text">
                  {mention.title !== "Publicação sem título" ? mention.title : mention.excerpt}
                </span>
              </span>
              <span className="review-queue-status">
                <span className={`review-dot ${polarityPalette(polarity)}`} title={polarity ? polarities.find(p => p.name === polarity)?.label ?? polarity : "Sem classificação"}/>
                {reviewed && <Check size={13} className="review-queue-check" aria-label="Revisada"/>}
              </span>
            </button>
          );
        })}
        <div className="review-queue-pager">
          <button type="button" disabled={!canPrevPage} onClick={() => { landAtEnd.current = true; onPrevPage(); }} aria-label="Página anterior">
            <ChevronLeft size={15}/>
          </button>
          <span className="review-queue-pager-info">
            <strong>{index + 1} de {mentions.length}</strong>
            <small>Página {page} de {pageCount} · {total.toLocaleString("pt-BR")} menções</small>
          </span>
          <ToolbarSelect
            ariaLabel="Menções por página"
            align="right"
            direction="up"
            value={String(pageSize)}
            onChange={value => onPageSize(Number(value))}
            options={pageSizes.map(size => ({ value: String(size), label: `${size} por página` }))}
          />
          <button type="button" disabled={!canNextPage} onClick={onNextPage} aria-label="Próxima página">
            <ChevronRight size={15}/>
          </button>
        </div>
      </aside>

      {base && view && platform && PlatformIcon && (
        <>
          <section className="review-focus" aria-label="Menção em revisão">
            {/* O topo da menção como no card da lista (2026-10-15): a rede
                e o veículo em primeiro plano, a data à direita; embaixo, o
                cliente (a "foto" e a marca). */}
            <header className="review-focus-head">
              <span
                className={`review-focus-logo net-${platform.tone} mention-data-tooltip`}
                data-tooltip={platform.label}
                data-tooltip-position="bottom"
                role="img"
                aria-label={platform.label}
              >
                {hasPlatformLogo(platform.tone) ? <PlatformLogo tone={platform.tone} size={16} mono/> : <PlatformIcon size={17} aria-hidden="true"/>}
              </span>
              <div className="review-focus-title">
                <strong>
                  {base.source || platform.label}
                  {view.reviewed && (
                    <span className="review-reviewed-badge">
                      <Check size={12} aria-hidden="true"/>
                      Revisada{base.reviewedByName ? ` por ${base.reviewedByName}` : ""}
                    </span>
                  )}
                </strong>
                <span className="review-focus-date">{base.date}</span>
              </div>
              {link && (
                <a className="review-focus-link" href={link} target="_blank" rel="noreferrer">
                  <ExternalLink size={14}/> Abrir conteúdo
                </a>
              )}
              <MentionActionsMenu
                mention={base}
                isOpen={actionMenuId === base.id}
                triggerSize={18}
                onToggle={() => onToggleActionMenu(base.id)}
                onReviewToggle={() => {
                  onCloseActionMenu();
                  void onToggleReviewed(base);
                }}
                onLink={() => {
                  onCloseActionMenu();
                  onLink(base);
                }}
                onDiscardToggle={() => {
                  onCloseActionMenu();
                  void onToggleDiscard(base);
                }}
              />
            </header>

            {base.brandName && (
              <div className="review-focus-client">
                <MentionClient
                  brandName={base.brandName}
                  companyName={base.companyName}
                  isCompetitor={base.isCompetitor}
                  logoUrl={base.isCompetitor ? null : companyLogoUrl}
                />
              </div>
            )}

            <div className="review-focus-content">
              <MentionPublisher mention={base} />
              {!untitled && (
                <h3><HighlightedText text={base.title} spans={base.highlights.title}/></h3>
              )}
              {showSummary ? (
                <SummaryText summary={summaryState.summary as string} origin={summaryState.origin}/>
              ) : (
                <p><HighlightedText text={base.excerpt} spans={base.highlights.content}/></p>
              )}
              {canGenerateSummary && <GenerateSummary state={summaryState}/>}
              <button type="button" className="review-fulltext-button" onClick={() => setFullTextOpen(true)}>
                <Maximize2 size={13} aria-hidden="true"/> Ver texto completo
              </button>
              {fullTextOpen && (
                <MentionFullTextDialog mention={base} token={token} onClose={() => setFullTextOpen(false)}/>
              )}
            </div>

            <div className="review-classify">
              {base.discardedAt && (
                <p className="form-hint">
                  Menção descartada — restaure pela lista ou pela Lixeira pra editar.
                </p>
              )}
              <div className="review-classify-row">
                <span className="review-classify-label">Polaridade</span>
                <div className="review-choice-group" role="group" aria-label="Polaridade">
                  {polarities.map(choice => (
                    <button
                      type="button"
                      key={choice.name}
                      className={`review-choice palette-${choice.palette}${view.polarity === choice.name ? " is-active" : ""}`}
                      aria-pressed={view.polarity === choice.name}
                      onClick={() => setPolarity(choice.name)}
                    >
                      {/* O mesmo rosto da classificação rápida da lista */}
                      {(() => {
                        const Face = paletteFaceIcon[choice.palette];
                        return <Face size={16} aria-hidden="true" className="review-choice-icon"/>;
                      })()}
                      {choice.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="review-classify-row">
                <span className="review-classify-label">Abordagem</span>
                <div className="review-choice-group" role="group" aria-label="Abordagem">
                  {APPROACHES.map(item => (
                    <button
                      type="button"
                      key={item.value}
                      className={`review-choice${view.approach === item.value ? " is-active" : ""}`}
                      aria-pressed={view.approach === item.value}
                      onClick={() => setApproach(item.value)}
                    >
                      {/* As estrelas da lista: 3 protagonista, 2 coadjuvante, 1 citação */}
                      <span className="review-choice-stars" aria-hidden="true">
                        {Array.from({ length: item.stars }, (_, n) => <Star key={n} size={12}/>)}
                      </span>
                      {item.label}
                    </button>
                  ))}
                </div>
              </div>
              {/* As mesmas três linhas da faixa do card: chip com × e o "+"
                  do catálogo. O pop-up do "+" tem `role="dialog"`, então os
                  atalhos de teclado param enquanto ele está aberto. */}
              {CATALOG_ROWS.map(({ kind, label }) => (
                <div className="review-classify-row is-catalog" key={kind}>
                  <span className="review-classify-label">{label}</span>
                  <MentionInlineCatalog
                    kind={kind}
                    mention={base}
                    token={token}
                    items={base[kind]}
                    loadOptions={() => loadCatalog(base).then(catalog => catalog[kind])}
                    onSaved={() => onReload(base.id)}
                    onError={onError}
                    locked={base.discardedAt !== null}
                  />
                </div>
              ))}
            </div>

            <footer className="review-actions">
              <button
                type="button"
                className={`review-primary${view.reviewed ? " is-done" : ""}`}
                onClick={() => void markReviewedAndNext()}
              >
                <Check size={15}/>
                {view.reviewed ? "Revisada" : "Marcar como revisada e seguir"}
              </button>
            </footer>
          </section>

          <aside className="review-context" aria-label="Detalhes da menção">
            <MentionDetailsBody key={base.id} mention={base} token={token} showClassification={false}/>
          </aside>
        </>
      )}
    </div>
  );
}
